import { useEffect, useState } from 'react'

import { ApiError, api, type Identity } from './api'
import { Fleet } from './Fleet'
import { Invite } from './Invite'
import { Login } from './Login'
import { Shared } from './Shared'
import { browserKnownLang, dict, isLang, initialLang, type Lang } from './i18n'
import { applyTheme, rememberTheme, savedTheme, watchSystem, type Theme } from './theme'

/** Адрес страницы по временной ссылке: /s/<ключ>. */
const SHARE_PREFIX = '/s/'
/** Адрес принятия приглашения: /invite/<токен>. */
const INVITE_PREFIX = '/invite/'

export function App() {
  // Ссылку открывает человек со стороны: ни сессии, ни настроек ему не нужно,
  // и спрашивать их до показа карты незачем.
  const shareKey = location.pathname.startsWith(SHARE_PREFIX)
    ? decodeURIComponent(location.pathname.slice(SHARE_PREFIX.length))
    : null
  const inviteToken = location.pathname.startsWith(INVITE_PREFIX)
    ? decodeURIComponent(location.pathname.slice(INVITE_PREFIX.length))
    : null

  const [identity, setIdentity] = useState<Identity | null>(null)
  const [lang, setLang] = useState<Lang>(() => initialLang('de'))
  const [theme, setTheme] = useState<Theme>(() => savedTheme())
  /**
   * Знает ли браузер, какой язык нужен этому человеку.
   *
   * Если нет — язык задаёт сервер, когда придут настройки. Считается один раз
   * при запуске: после того как человек выберет язык глобусом, «не знали» уже
   * неправда, и перебивать его выбор настройкой сервера нельзя.
   */
  const [langKnown] = useState(() => browserKnownLang() !== null)
  const [center, setCenter] = useState({ lat: 52.52, lon: 13.405 })
  const [maps, setMaps] = useState(false)
  // Предел глубины трека приходит с сервера. До ответа берём его же
  // умолчание: показать кнопку, которая заведомо откажет, хуже, чем
  // на секунду не показать её вовсе.
  const [trackDays, setTrackDays] = useState(7)
  const [ready, setReady] = useState(false)

  // При открытии вкладки проверяем, жива ли сессия: перезагрузка страницы
  // не должна выбрасывать человека на форму входа.
  useEffect(() => {
    let cancelled = false
    async function boot() {
      try {
        const config = await api.config()
        if (!cancelled) {
          setCenter({ lat: config.mapCenter.lat, lon: config.mapCenter.lon })
          setMaps(config.maps)
          if (config.trackDays) setTrackDays(config.trackDays)
          // Браузер не назвал знакомого языка и человек ничего не выбирал —
          // значит решает настройка сервера, а не зашитое в код умолчание.
          if (!langKnown && isLang(config.defaultLanguage)) setLang(config.defaultLanguage)
        }
      } catch {
        // настройки не пришли — останутся значения по умолчанию
      }
      try {
        const me = await api.me()
        if (!cancelled) setIdentity(me)
      } catch (caught) {
        // 401 — обычное дело: сессии нет, покажем вход
        if (!(caught instanceof ApiError)) throw caught
      }
      if (!cancelled) setReady(true)
    }
    void boot()
    return () => {
      cancelled = true
    }
  }, [])

  // Тема применяется к странице сразу и заново при смене системной, пока
  // выбрано «как в системе». Сохраняется выбор, а не результат.
  useEffect(() => {
    applyTheme(theme)
    rememberTheme(theme)
    if (theme !== 'system') return
    return watchSystem(() => applyTheme(theme))
  }, [theme])

  useEffect(() => {
    document.documentElement.lang = lang
    // Заголовок вкладки тоже идёт за языком. В index.html он статичный и
    // русский: немец видел бы кириллицу во вкладке, в закладках и в истории
    // браузера — там, где до наших переводов дело не доходит.
    document.title = dict(lang).appName
  }, [lang])

  // Приглашение, как и ссылка на машину, открывается без входа. Caddy отдаёт
  // index.html на любой неизвестный путь, так что /invite/… доходит сюда.
  if (inviteToken) {
    return <Invite token={inviteToken} lang={lang} onLang={setLang} center={center} />
  }

  if (shareKey) return <Shared shareKey={shareKey} lang={lang} maps={maps} />

  if (!ready) return null

  if (!identity) {
    return <Login lang={lang} onLang={setLang} center={center} onSignedIn={setIdentity} />
  }

  return (
    <Fleet
      identity={identity}
      lang={lang}
      center={center}
      maps={maps}
      trackDays={trackDays}
      theme={theme}
      onTheme={setTheme}
      onSignedOut={() => setIdentity(null)}
      onIdentity={setIdentity}
    />
  )
}
